import { useLayoutEffect, useMemo, useState } from 'react';

import { useAmplitude } from '@crehana/analytics';
import { Paginator } from '@crehana/compass.ui/paginator';
import {
  Table,
  TableBody,
  TableCell,
  TableContent,
  TableEmptyResults,
  TableFooter,
  TableHead,
  TableHeader,
  TableLoading,
  TableMainHeader,
  TableRow,
  TableSearch,
  TableTitleAndCounter,
  TableTop,
} from '@crehana/compass.ui/table';
import {
  flexRender,
  type PaginationState,
  type SearchState,
  useTable,
} from '@crehana/compass.ui/table/tanstack';

import { useCourseDetailTranslation } from '../../../../hooks/useCourseDetailTranslation';
import {
  type TEnrollmentsTableUserColumn,
  useEnrollmentsByCourse,
} from '../../hooks/use-enrollments-by-course';
import { useEnrollmentsTableColumns } from './hooks/use-enrollments-table-columns';

type TEnrollmentsCurrentCourseTableProps = {
  courseId: string;
  courseTitle: string;
  pageSize: number;
};

export const EnrollmentsCurrentCourseTable: React.FC<
  TEnrollmentsCurrentCourseTableProps
> = ({ courseId, courseTitle, pageSize }) => {
  const { logEvent } = useAmplitude();
  const { t } = useCourseDetailTranslation();
  const columns = useEnrollmentsTableColumns();
  const [search, setSearch] = useState<SearchState>('');
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const {
    enrollments,
    totalEnrollments,
    loading: loadingEnrollments,
  } = useEnrollmentsByCourse({
    pageSize,
    searchQuery: search,
    courseId: Number(courseId),
    currentPage: pagination.pageIndex + 1,
    apolloConfig: {
      notifyOnNetworkStatusChange: true,
    },
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  const transformedEnrollments = useMemo<TEnrollmentsTableUserColumn[]>(() => {
    return enrollments.map(
      enrollment =>
        ({
          id: enrollment.centralizedUserId,
          assignedRole: t(
            'ENROLLMENTS_CURRENT_COURSE_DIALOG_TABLE_ROLE_ADMINISTRATOR',
          ), // TODO: get assigned role from backend when it's ready
          enrollment,
        }) satisfies TEnrollmentsTableUserColumn,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollments]);

  const table = useTable({
    data: transformedEnrollments,
    rowCount: totalEnrollments,
    columns,
    onSearchChange: setSearch,
    onPaginationChange: setPagination,
    state: {
      pagination,
      search,
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useLayoutEffect(() => {
    if (!loadingEnrollments) {
      logEvent({
        event: 'Content Detail Leader Users List',
        data: {
          'Content ID': courseId,
          'Content Name': courseTitle,
          '# User Available': totalEnrollments,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalEnrollments, loadingEnrollments]);

  const renderTableContent = () => {
    if (loadingEnrollments) {
      return <TableLoading pageSize={1} />;
    }

    if (table.getRowCount() === 0) {
      return <TableEmptyResults pageSize={1} />;
    }

    return (
      <TableBody>
        {table.getRowModel().rows.map(row => (
          <TableRow key={row.id}>
            {row.getVisibleCells().map(cell => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    );
  };

  return (
    <Table>
      <TableTop>
        <TableMainHeader>
          <TableTitleAndCounter
            count={table.getRowCount()}
            title={t('ENROLLMENTS_CURRENT_COURSE_DIALOG_TABLE_TITLE')}
          />
          <TableSearch
            placeholder={t('ENROLLMENTS_CURRENT_COURSE_DIALOG_TABLE_SEARCH')}
            value={table.searchState}
            onSearch={table.searchRows}
          />
        </TableMainHeader>
      </TableTop>
      <TableContent className="tw-border-t-0">
        <TableHeader>
          {table.getHeaderGroups().map(headerGroup => (
            <TableRow isHeader key={headerGroup?.id}>
              {headerGroup.headers.map(header => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        {renderTableContent()}
        {!loadingEnrollments && table.getRowCount() > 0 && (
          <TableFooter>
            <Paginator
              variant="numeric"
              currentPage={table.currentPage + 1}
              totalPages={table.getRowCount() / table.pageSize}
              onPageChange={page => table.setPageIndex(page - 1)}
            />
          </TableFooter>
        )}
      </TableContent>
    </Table>
  );
};
